import { generatePath } from 'react-router-dom';
import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { addParamsToUrl } from 'utils/query';
import { calculateOffset } from 'utils/helpers';
import { filterEmptyFields } from 'utils/object';
import { cartApi, tagTypes as cartTagTypes } from 'store/api/cart';
import { storagesApi, tagTypes as storagesApiTagType } from 'store/api/storage';

import { apiQuery } from '../../query';

import {
  Event,
  EventReport,
  EventStatus,
  EventCheckout,
  EventLocation,
  EventDayResult,
  EventTopSeller,
  EventFormValues,
  EventTeamMember,
  CashManagerType,
  ReceiptResponse,
  ReceiptsRequest,
  EmployeeCheckout,
  EventListRequest,
  TopEventsRequest,
  DailyCountProduct,
  ReportPdfResponse,
  EventListResponse,
  TopEventsResponse,
  EventQuestionnaire,
  EventReportRequest,
  ReceiptHTMLRequest,
  EventReportProduct,
  EventProfitAndLoss,
  TotalSalesForEvent,
  EventReportEmployee,
  EventReportResponse,
  EventCheckoutRequest,
  eventPaymentTypeLabel,
  EventListResponseData,
  ReceiptDetailsRequest,
  EventTopSellerRequest,
  EventEmployeesResponse,
  EventTopSellerResponse,
  eventPaymentTypeOptions,
  EmployeeCheckoutRequest,
  EventInventoryStatistic,
  EventEmployeeCommission,
  EventDashboardStatistics,
  EventOpenCloseDayRequest,
  EventCashManagerResponse,
  TotalSalesForEventRequest,
  EventQuestionnaireRequest,
  AdditionalCashManagerType,
  eventPaymentTransactionLabel,
  EventStockOrdersInfoResponse,
  eventPaymentTransactionOptions,
  EventDashboardStatisticsRequest,
  UpdateEventQuestionnaireRequest,
  EventEmployeeCommissionsRequest,
  CreateShippingForExistingCheckoutRequest,
} from './types';

const tagTypes = ['Event'];

export const eventApi = createApi({
  tagTypes,
  baseQuery: apiQuery,
  reducerPath: 'eventApi',
  endpoints: (build) => ({
    remove: build.mutation<void, string>({
      query: (id) => ({
        method: 'delete',
        url: URL.EVENT_DETAILS.replace(':id', id),
      }),
    }),

    questionnaires: build.query<EventQuestionnaire[], void>({
      providesTags: tagTypes,
      query: () => ({
        method: 'get',
        url: URL.EVENT_GET_QUESTIONNAIRES,
      }),
    }),

    checkoutInfo: build.query<EventCheckout, string>({
      providesTags: tagTypes,
      query: (id) => ({
        method: 'get',
        url: URL.EVENT_CHECKOUT.replace(':id', id),
      }),
    }),

    employeeEvents: build.query<Event[], number>({
      providesTags: tagTypes,
      query: (id) => ({
        method: 'get',
        url: URL.EMPLOYEE_EVENTS.replace(':id', String(id)),
      }),
    }),

    employees: build.query<EventEmployeesResponse[], string>({
      providesTags: tagTypes,
      query: (id) => ({
        method: 'get',
        url: URL.EVENT_EMPLOYEES.replace(':id', id),
      }),
    }),

    cashManager: build.query<EventCashManagerResponse, string>({
      providesTags: tagTypes,
      query: (id) => ({
        method: 'get',
        url: URL.EVENT_CASH_MANAGER.replace(':id', id),
      }),
    }),

    details: build.query<Event, string>({
      keepUnusedDataFor: 0,
      providesTags: tagTypes,
      query: (id) => ({
        method: 'get',
        url: URL.EVENT_DETAILS.replace(':id', id),
      }),
    }),

    voidedUser: build.query<EventCheckout[], string>({
      providesTags: tagTypes,
      query: (id) => ({
        method: 'get',
        url: generatePath(URL.GET_VOIDED_CHECKOUTS_FOR_USER, { id }),
      }),
    }),

    getQuestionnaire: build.query<EventQuestionnaire, string>({
      providesTags: tagTypes,
      query: (id) => ({
        method: 'get',
        url: URL.EVENT_GET_QUESTIONNAIRE.replace(':id', id),
      }),
    }),

    topEvents: build.query<TopEventsResponse, TopEventsRequest>({
      providesTags: tagTypes,
      query: (query) => ({
        method: 'get',
        url: addParamsToUrl(URL.GET_TOP_EVENTS, query),
      }),
    }),

    deleteQuestionnaire: build.mutation<void, string>({
      invalidatesTags: tagTypes,
      query: (id) => ({
        method: 'delete',
        url: URL.EVENT_GET_QUESTIONNAIRE.replace(':id', String(id)),
      }),
    }),

    profitAndLosses: build.query<EventProfitAndLoss, string>({
      providesTags: tagTypes,
      query: (id) => ({
        method: 'get',
        url: URL.EVENT_PROFIT_AND_LOSSES.replace(':id', String(id)),
      }),
    }),

    inventoryStatistic: build.query<EventInventoryStatistic, string>({
      providesTags: tagTypes,
      query: (id) => ({
        method: 'get',
        url: URL.EVENT_INVENTORY_STATISTIC.replace(':id', id),
      }),
    }),

    stockOrdersInfo: build.query<EventStockOrdersInfoResponse, string>({
      providesTags: tagTypes,
      query: (id) => ({
        method: 'get',
        url: URL.EVENT_STOCK_ORDERS_INFO.replace(':id', String(id)),
      }),
    }),

    offer: build.mutation<void, Pick<Event, 'id' | 'companyId'>>({
      invalidatesTags: tagTypes,
      query: ({ id, ...data }) => ({
        data,
        method: 'patch',
        url: URL.EVENT_OFFER.replace(':id', String(id)),
      }),
    }),

    openCloseDay: build.mutation<Event, EventOpenCloseDayRequest>({
      invalidatesTags: tagTypes,
      query: ({ id, ...data }) => ({
        data,
        method: 'post',
        url: URL.EVENT_CLOSE_DAY.replace(':id', String(id)),
      }),
    }),

    topSellers: build.query<
      EventTopSellerResponse,
      EventTopSellerRequest | void
    >({
      providesTags: tagTypes,
      query: (params) => ({
        method: 'get',
        url: addParamsToUrl(URL.EVENT_TOP_SELLERS, params || {}),
      }),
    }),

    html: build.query<string, ReceiptHTMLRequest>({
      query: ({ id, receiptId, ...params }) => ({
        method: 'get',
        url: addParamsToUrl(
          generatePath(URL.EVENT_RECEIPTS_HTML, { id, receiptId }),
          params
        ),
      }),
    }),

    report: build.query<EventReportResponse, EventReportRequest>({
      providesTags: tagTypes,
      query: ({ id, isDailyReport }) => ({
        method: 'get',
        url: addParamsToUrl(URL.EVENT_REPORT.replace(':id', id), {
          isDailyReport,
        }),
      }),
    }),

    questionnaire: build.mutation<
      EventQuestionnaire,
      EventQuestionnaireRequest
    >({
      invalidatesTags: tagTypes,
      query: ({ id, ...data }) => ({
        data,
        method: 'post',
        url: URL.EVENT_QUESTIONNAIRE.replace(':id', String(id)),
      }),
    }),

    totalSalesForEvents: build.query<
      TotalSalesForEvent,
      TotalSalesForEventRequest
    >({
      providesTags: tagTypes,
      query: ({ eventIds }) => ({
        method: 'get',
        url: addParamsToUrl(URL.EVENT_TOTAL_SALES, {
          eventIds,
        }),
      }),
    }),

    generateReportPdf: build.query<ReportPdfResponse, EventReportRequest>({
      providesTags: tagTypes,
      query: ({ id, isDailyReport }) => ({
        method: 'get',
        url: addParamsToUrl(URL.EVENT_REPORT_PDF.replace(':id', id), {
          isDailyReport,
        }),
      }),
    }),

    receiptDetails: build.query<EventCheckout, ReceiptDetailsRequest>({
      providesTags: tagTypes,
      query: ({ id, receiptId }) => ({
        method: 'get',
        url: URL.EVENT_RECEIPT.replace(':id', String(id)).replace(
          ':receiptId',
          receiptId
        ),
      }),
    }),

    updateQuestionnaire: build.mutation<
      EventQuestionnaire,
      UpdateEventQuestionnaireRequest
    >({
      invalidatesTags: tagTypes,
      query: ({ id, ...data }) => ({
        data,
        method: 'patch',
        url: URL.EVENT_GET_QUESTIONNAIRE.replace(':id', String(id)),
      }),
    }),

    employeeCheckouts: build.query<EmployeeCheckout, EmployeeCheckoutRequest>({
      providesTags: tagTypes,
      query: ({ id, ...params }) => ({
        method: 'get',
        url: addParamsToUrl(
          URL.EVENT_EMPLOYEE_CHECKOUTS.replace(':id', String(id)),
          params
        ),
      }),
    }),

    employeeCommissionsInfo: build.query<
      EventEmployeeCommission,
      EventEmployeeCommissionsRequest
    >({
      providesTags: tagTypes,
      query: ({ id, userId }) => ({
        method: 'get',
        url: generatePath(URL.EVENT_EMPLOYEE_COMMISSIONS, {
          id: String(id),
          userId: String(userId),
        }),
      }),
    }),

    dashboardStatistics: build.query<
      EventDashboardStatistics,
      EventDashboardStatisticsRequest
    >({
      providesTags: tagTypes,
      query: ({ id, endDate, startDate }) => ({
        method: 'get',
        url: addParamsToUrl(
          generatePath(URL.EVENT_DASHBOARD_STATISTICS, { id }),
          { endDate, startDate }
        ),
      }),
    }),

    create: build.mutation<Event, EventFormValues>({
      invalidatesTags: tagTypes,
      query: ({ locations, ...data }) => ({
        method: 'post',
        url: URL.EVENT_LIST,
        data: filterEmptyFields(
          {
            ...data,
            locations: locations.filter(
              (location) => Object.values(location).length
            ),
          },
          ['isManualCreditPaymentEnabled']
        ),
      }),
    }),

    createShipping: build.mutation<
      EventCheckout,
      CreateShippingForExistingCheckoutRequest
    >({
      query: ({ id, ...data }) => ({
        data,
        method: 'post',
        url: URL.EVENT_CREATE_SHIPPING.replace(':id', String(id)),
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(eventApi.util.invalidateTags(tagTypes));
        } catch {
          /* empty */
        }
      },
    }),

    checkout: build.mutation<EventCheckout, EventCheckoutRequest>({
      invalidatesTags: tagTypes,
      query: ({ id, ...data }) => ({
        data,
        method: 'post',
        url: URL.EVENT_CHECKOUT.replace(':id', String(id)),
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(cartApi.util.invalidateTags(cartTagTypes));
        } catch (error) {
          console.error(error);
        }
      },
    }),

    receipts: build.query<ReceiptResponse, ReceiptsRequest>({
      providesTags: tagTypes,
      query: (params) => {
        const { id, page = 1, limit = 20, ...otherParams } = params || {};
        const offset = calculateOffset(page, limit);
        const searchParams = {
          limit,
          offset,
          ...otherParams,
        };

        return {
          method: 'get',
          url: addParamsToUrl(
            URL.EVENT_RECEIPTS.replace(':id', String(id)),
            searchParams
          ),
        };
      },
    }),

    edit: build.mutation<Event, { id: string } & Partial<EventFormValues>>({
      invalidatesTags: tagTypes,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      query: ({ id, locations, companyId, ...data }) => ({
        method: 'patch',
        url: URL.EVENT_DETAILS.replace(':id', id),
        data: filterEmptyFields(
          {
            ...data,
            locations: locations?.filter(
              (location) => Object.values(location).length
            ),
          },
          ['isManualCreditPaymentEnabled']
        ),
      }),
    }),

    list: build.query<EventListResponse, EventListRequest | void>({
      providesTags: tagTypes,
      transformResponse: (response: EventListResponseData) => response.data,
      query: (params) => {
        const { page = 1, limit = 20, ...otherParams } = params || {};
        const offset = calculateOffset(page, limit);
        const searchParams = {
          limit,
          offset,
          ...otherParams,
        };
        return {
          method: 'get',
          url: params
            ? addParamsToUrl(URL.EVENT_LIST, searchParams)
            : URL.EVENT_LIST,
        };
      },
    }),

    accept: build.mutation<void, Pick<Event, 'id'> & { locationIds: number[] }>(
      {
        invalidatesTags: tagTypes,
        query: ({ id, locationIds }) => ({
          method: 'patch',
          data: {
            locationIds,
          },
          url: URL.EVENT_ACCEPT.replace(':id', String(id)),
        }),
        onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
          try {
            await queryFulfilled;

            dispatch(storagesApi.util.invalidateTags(storagesApiTagType));
          } catch (error) {
            console.error(error);
          }
        },
      }
    ),
  }),
});

export const {
  useListQuery,
  useHtmlQuery,
  useReportQuery,
  useDetailsQuery,
  useEditMutation,
  useOfferMutation,
  useReceiptsQuery,
  useTopEventsQuery,
  useAcceptMutation,
  useRemoveMutation,
  useEmployeesQuery,
  useCreateMutation,
  useTopSellersQuery,
  useVoidedUserQuery,
  useCashManagerQuery,
  useCheckoutMutation,
  useCheckoutInfoQuery,
  useQuestionnairesQuery,
  useEmployeeEventsQuery,
  useReceiptDetailsQuery,
  useStockOrdersInfoQuery,
  useProfitAndLossesQuery,
  useOpenCloseDayMutation,
  useGetQuestionnaireQuery,
  useLazyCheckoutInfoQuery,
  useQuestionnaireMutation,
  useCreateShippingMutation,
  useEmployeeCheckoutsQuery,
  useGenerateReportPdfQuery,
  useInventoryStatisticQuery,
  useTotalSalesForEventsQuery,
  useDashboardStatisticsQuery,
  useLazyGenerateReportPdfQuery,
  useDeleteQuestionnaireMutation,
  useUpdateQuestionnaireMutation,
  useEmployeeCommissionsInfoQuery,
} = eventApi;

export type {
  Event,
  EventStatus,
  EventReport,
  EventCheckout,
  EventLocation,
  EventDayResult,
  EventTopSeller,
  EventFormValues,
  EventTeamMember,
  EmployeeCheckout,
  DailyCountProduct,
  EventQuestionnaire,
  EventProfitAndLoss,
  EventReportProduct,
  EventReportResponse,
  EventReportEmployee,
  ReceiptDetailsRequest,
  EventEmployeeCommission,
  EventDashboardStatistics,
  AdditionalCashManagerType,
};
export {
  tagTypes,
  CashManagerType,
  eventPaymentTypeLabel,
  eventPaymentTypeOptions,
  eventPaymentTransactionLabel,
  eventPaymentTransactionOptions,
};
